import React, { useState, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ProSidebarProvider } from "react-pro-sidebar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Routes and Pages
import AppRoutes from "./Routes/index";
import Login from "./Pages/Authentication/Login";
import Register from "./Pages/Authentication/Register";
import ResetPassword from "./Pages/Authentication/ResetPassowrd";
import VerifyCertificate from "./Pages/VerifyCertificate";
import VerifyEmail from "./Pages/VerifyEmail";
import MaintenancePage from "./Pages/MaintenancePage";
import Learning from "./Pages/Competancy/Learning";
import GuestHazardForm from "./Pages/Incident/GuestHazard";

// Context and Utils
import { NavigationProvider } from "./Navigation";
import { startVersionChecker } from "./utils/versionChecker";

// Styles
import './assets/scss/theme.scss';

const EXEMPT_PATHS = ['/verify-certificate', '/verify'];


const configureVersionChecker = () => {
  startVersionChecker((newVersion) => {
    const refreshApp = () => window.location.reload();
    const toastId = "new-version-toast";

    if (toast.isActive(toastId)) {
      toast.dismiss(toastId);
    }

    setTimeout(() => {
      toast.info(
        <div style={{ padding: '10px' }}>
          <h5 style={{ marginBottom: '10px', fontWeight: 'bold' }}>
            <span role="img" aria-label="update" style={{ marginRight: '8px' }}>&#11014;&#65039;</span>
            M-SPHERE New Version Available!
          </h5>
          <p style={{ marginBottom: '15px' }}>
            Version <strong>v{newVersion}</strong> is ready. Please refresh to get the latest features and improvements.
          </p>
          <button 
            onClick={refreshApp}
            className="btn btn-primary w-100"
            style={{ fontWeight: 'bold' }}
          >
            Refresh to Update
          </button>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: false,
          closeButton: true,
          toastId: toastId,
          style: {
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }
        }
      );
    }, 100);
  });
};

// Initialize version checker
configureVersionChecker();

const MaintenanceWrapper = ({ children }) => {
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();

    useEffect(() => {
        const checkMaintenanceStatus = async () => {
            try {
                const response = await fetch(`/maintenance.json?t=${new Date().getTime()}`);
                const data = await response.json();
                setMaintenanceMode(data.maintenance_mode === 1);
            } catch (error) {
                console.error('Error checking maintenance status:', error);
                setMaintenanceMode(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkMaintenanceStatus();
        const interval = setInterval(checkMaintenanceStatus, 10000);
        return () => clearInterval(interval);
    }, []);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (maintenanceMode && !EXEMPT_PATHS.includes(location.pathname)) {
        return <MaintenancePage />;
    }

    return children;
};

const App = () => (
    <React.Fragment>
        <MaintenanceWrapper>
            <ProSidebarProvider>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/verify-certificate" element={<VerifyCertificate />} />
                    <Route path="/hazard/public" element={<GuestHazardForm />} />
                    <Route path="/verify" element={<VerifyEmail />} />
                    <Route path="/learn/:courseKey" element={<Learning />} />
                    <Route
                        path="/*"
                        element={
                            <NavigationProvider>
                                <AppRoutes />
                            </NavigationProvider>
                        }
                    />
                </Routes>
                <ToastContainer />
            </ProSidebarProvider>
        </MaintenanceWrapper>
    </React.Fragment>
);

export default App;